/*
 * A tag, and its removable form: an applied filter, a project label, a locale
 * on a knowledge document.
 *
 * `removeLabel` is required by the type whenever `onRemove` is passed, the same
 * trick Button uses for its icon-only shape. The alternative, deriving the name
 * from `children`, produces "Remove [object Object]" the first time somebody
 * puts a Badge or a formatted date inside a tag, and nothing fails until a
 * screen reader reads it out.
 *
 * What this component cannot do is manage focus after removal, because it does
 * not know what its siblings are. The caller owns that: when a tag is removed
 * by keyboard, focus has to be moved to the next tag or to the container, or it
 * falls to `<body>` and the person is returned to the top of the document.
 */

import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'
import { CrossMark } from '../marks'
import styles from './Tag.module.css'

interface TagBaseProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  children: ReactNode
  /** A leading mark in the caller's colour, for a tag that stands for a series. */
  swatch?: string
}

export type TagProps =
  | (TagBaseProps & { onRemove?: undefined; removeLabel?: undefined })
  | (TagBaseProps & { onRemove(): void; removeLabel: string })

export function Tag(props: TagProps) {
  const { children, swatch, onRemove, removeLabel, className, ...rest } = props

  const tagClassName = [styles.tag, onRemove ? styles.removable : '', className]
    .filter(Boolean)
    .join(' ')

  return (
    <span {...rest} className={tagClassName}>
      {swatch === undefined ? null : (
        // A custom property is the one inline style this codebase allows,
        // because it is the only way a runtime value can reach a stylesheet.
        <span
          className={styles.swatch}
          style={{ '--tag-swatch': swatch } as CSSProperties}
          aria-hidden="true"
        />
      )}
      <span className={styles.label}>{children}</span>
      {onRemove ? (
        <button type="button" className={styles.remove} onClick={onRemove} aria-label={removeLabel}>
          <CrossMark size={12} />
        </button>
      ) : null}
    </span>
  )
}
